import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { BattleField } from './BattleField';
import { BattleControls } from './BattleControls';
import { BattleHistory } from './BattleHistory';
import { BattleEndScreen } from './BattleEndScreen';
import { useAiBattle } from '../../hooks/useAiBattle';
import { useBattle } from '../../hooks/useBattle';
import type { User } from '../../types/user';
import type { UserPet } from '../../types/pets';
import type { UserItem } from '../../types/items';
import type { BattleMoveType } from '../../types/battle';

interface BattleArenaProps {
  user: User | null;
  pets: UserPet[];
  items: UserItem[];
  wagerAmount: number;
  onBack: () => void;
}

export function BattleArena({ user, pets, items, wagerAmount, onBack }: BattleArenaProps) {
  const [status, setStatus] = useState<'searching' | 'opponent_found' | 'battling'>('searching');
  const { battleState, lastEffect, isProcessing, error, startBattle, makeMove } = useAiBattle(user);

  // Handle search and opponent found sequence
  useEffect(() => {
    if (status === 'searching') {
      const searchTime = Math.random() * 2000 + 3000;
      const timer = setTimeout(() => {
        setStatus('opponent_found');
        
        setTimeout(() => {
          startBattle(pets, items, wagerAmount);
          setStatus('battling');
        }, 1500);
      }, searchTime);

      return () => clearTimeout(timer);
    }
  }, [status, pets, items, wagerAmount, startBattle]);

  const handleMove = (type: BattleMoveType) => {
    if (!battleState || isProcessing) return;
    
    const power = type === 'attack' ? 10 : 
                 type === 'defend' ? 5 : 15;
                 
    makeMove({ type, power, pet: pets[0] });
  };

  const handleUseItem = (item: UserItem) => {
    if (!battleState || isProcessing) return;
    
    makeMove({ 
      type: 'attack', 
      power: 8,
      item,
      pet: pets[0]
    });
  };

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-center">
        <p className="text-red-400">{error}</p>
        <button
          onClick={onBack}
          className="mt-4 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
        >
          Return to Menu
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-yellow-500 hover:text-yellow-400"
      >
        <ArrowLeft className="w-4 h-4" />
        Cancel Battle
      </button>

      <div className="space-y-6">
        {status === 'searching' && (
          <div className="bg-gray-900 rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Searching for Opponent</h2>
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500" />
              <p className="text-gray-400">Matching you with a worthy challenger...</p>
            </div>
          </div>
        )}

        {status === 'opponent_found' && (
          <div className="bg-gray-900 rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Opponent Found!</h2>
            <p className="text-gray-400">Preparing for battle...</p>
          </div>
        )}

        {status === 'battling' && battleState && (
          <div className="space-y-6">
            <BattleField
              playerPet={pets[0]}
              aiPet={battleState.aiPets[0]}
              playerHealth={battleState.playerHealth}
              aiHealth={battleState.aiHealth}
              playerItems={items}
              aiItems={battleState.aiItems}
              isPlayerTurn={battleState.currentTurn === 'player'}
              lastEffect={lastEffect}
            />
            
            <BattleControls
              onMove={handleMove}
              onUseItem={handleUseItem}
              items={items}
              disabled={battleState.currentTurn !== 'player' || isProcessing}
              specialAttacksUsed={battleState.specialAttacksUsed.player}
            />

            {battleState.moves.length > 0 && (
              <BattleHistory 
                moves={battleState.moves}
                effects={[]}
              />
            )}

            {battleState.gameOver && (
              <BattleEndScreen
                winner={battleState.playerHealth <= 0 ? 'ai' : 'player'}
                wagerAmount={wagerAmount}
                onPlayAgain={() => {
                  setStatus('searching');
                }}
                onExit={onBack}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}